<script>
export default {
	name: 'SubTabRedeemFlight',
};
</script>

<script setup>
import RadioButton from '~/components/form/RadioButton.vue';
import SubTabBookFlight from '~/components/BookingWidget/FlightSearch/SubTabBookFlight.vue';
import { i18nGlobal } from '~/logic/i18n.js';

import AppSpinner from '~/components/AppSpinner.vue';
import { useAuthStore } from '~/logic/auth/auth-store.js';
import { useBookingWidgetUrlQuery } from '~/logic/composables/booking-widget/useBookingWidgetUrlQuery.js';
import { isMobileViewport } from '~/logic/composables/breakpoints.js';

const props = defineProps({
	hideTodayDateIndicator: { type: Boolean, default: false },
	// one world props
	owGeneralDetails: { type: Object, default: null },
	owOndSelection: { type: Object, default: null },
	owPassengerSelection: { type: Object, default: null },
	owDatePicker: { type: Object, default: null },
	owPromoCode: { type: Object, default: null },
	owEnrichAlwaysOn: { type: Object, default: null },
	// mh props
	mhGeneralDetails: { type: Object, default: null },
	mhOndSelection: { type: Object, default: null },
	mhPassengerSelection: { type: Object, default: null },
	mhDatePicker: { type: Object, default: null },
	mhPromoCode: { type: Object, default: null },
	mhEnrichAlwaysOn: { type: Object, default: null },
	
	disableGeolocation: { type: Boolean, default: false },
	subHeaderTooltipCTATextMobileLabel: { type: String, default: '' },
	fromQuestionMobile: { type: String, default: '' },
	toQuestionMobile: { type: String, default: '' },
	datePickerQuestionMobile: { type: String, default: '' },
	disableCalendarPickerAnimation: { type: Boolean, default: false },
	passengerSelectionQuestion: { type: String, default: '' },
	flightSearchApiUrl: { type: String, default: '' },
	cabinClassCashList: { type: Array, default: () => [] },
	cabinClassMilesList: { type: Array, default: () => [] },
	componentIdAA: { type: String, default: '' },
	togglePointsApiURL: { type: String, default: '' },
	enableMattaToggleButton: { type: Boolean, default: false },
	mattaToggleButtonLabel: { type: String, default: '' },
	enableONDLocaleTranslation: { type: Boolean, default: false },
	countryVariants: { type: Array, default: () => [] },
	payByEnrichPoints: { type: Boolean, default: false },
	cabinClassMilesOWList: { type: Array, default: () => [] },
	enableGlassDesign: { type: Boolean, default: false },

	oneWorldLabel: { type: String, default: '' },
	malaysiaAirlinesLabel: { type: String, default: '' },
});

const emit = defineEmits([
	'save-search-data',
	'show-bst-overlay',
	'form-submission',
	'update:active-sub-tab',
]);

const {
	isLoggedIn,
} = useAuthStore();

const activeSubOption = ref(null);
const activeSubTab = ref('redeem-flight');

const subTabMalaysiaRef = ref(null);
const subTabOneWorldRef = ref(null);

const currentSubTabRef = computed(() => {
	return activeSubOption.value === 'malaysia-airlines'
		? subTabMalaysiaRef.value
		: subTabOneWorldRef.value;
});

const restorePastSearch = async (searchData) => {
	activeSubOption.value = searchData.redeemFlightType;
	await currentSubTabRef.value?.restorePastSearch(searchData);
};
const updateLocationFromTo = async (payload) => {
	currentSubTabRef.value?.updateLocationFromTo(searchData);
};
const isExpanded = async (payload) => {
	currentSubTabRef.value?.isExpanded(searchData);
};

const selectedFlightOption = computed(() => {
	return getRedeemTabFlightOptions.value.find( (option) => option.value === activeSubOption.value) || {};
});

const getRedeemTabFlightOptions = computed(() => {
	const redeemSubTabOptions = [
		{
			key: isMobileViewport.value ? props.mhGeneralDetails?.isHideInMobile : props.mhGeneralDetails?.isHideInDesktop,
			text: props.mhGeneralDetails?.redeemFlightMalaysiaAirlinesLabel, value: 'malaysia-airlines',
		}, {
			key: isMobileViewport.value ? props.owGeneralDetails?.isHideInMobile : props.owGeneralDetails?.isHideInDesktop,
			text: props.owGeneralDetails?.redeemFlightOneWorldLabel, value: 'ow-redemption',
		},
	];
	
	return redeemSubTabOptions.filter((option) => !option.key);
});

const handleQueryChange = (query) => {
	if (!query) return;

	const newSubTab = query.subtab ?? '';
	const newSubOption = query.suboption ?? '';
	if (newSubTab) {
		activeSubTab.value = newSubTab;
		emit('update:active-sub-tab', newSubTab);
	}
	if (newSubOption) {
		activeSubOption.value = ['malaysia-airlines', 'ow-redemption'].includes(newSubOption)
			? query.suboption
			: (getRedeemTabFlightOptions.value.length > 0 ? getRedeemTabFlightOptions.value[0].value : '');
	}
};

const setupTabWatcher = (mainTabName, handler) => {
	const { registerQueryChange } = useBookingWidgetUrlQuery({ mainTabName });
	registerQueryChange(handler);
};

setupTabWatcher('flight-search-tab', handleQueryChange);
setupTabWatcher('enrich-flight-search', handleQueryChange);
onMounted(() => {
	nextTick(() => {
		// only set default if URL/watchers didn’t already set it
		if (!activeSubOption.value) {
			const availableOptions = getRedeemTabFlightOptions.value;
			if (availableOptions.length > 0) {
				activeSubOption.value = availableOptions[0].value;
			}
		}
	});
});


defineExpose({
	restorePastSearch,
	updateLocationFromTo,
	isExpanded,
});


</script>

<template>
<div
	ref="rootEl"
	v-focus-within
	class="SubTabRedeemFlight flight-search-subcomponent"
	tabindex="-1"
	:class="{
		'isEditorMode': isEditorMode,
	}"
	@focus-within="handleRootFocusWithin"
	@blur-within="handleRootBlurWithin"
>
	<div>
		<h5 v-if="isEditorMode" class="title-editor-mode">
			<icon-fas-plane class="mr-2 fill-current inline-block" />
			<span>Booking Widget: Flight Search</span>
		</h5>
		<div>
			<div class="flex space-x-4">
				<RadioButton
					v-model="activeSubOption"
					name="redeem-flight-options"
					:items="getRedeemTabFlightOptions"
					:layoutAttrs="{ class: '!flex' }"
					class="redeemOptions"
				>
				</RadioButton>
			</div>
			<!-- Content Panels -->
			<div v-if="getRedeemTabFlightOptions.length > 0">
				<SubTabBookFlight
					v-if="getRedeemTabFlightOptions.some(opt => opt.value === 'malaysia-airlines')"
					v-show="activeSubOption === 'malaysia-airlines'"
					ref="subTabMalaysiaRef"
					:activeSubOption="selectedFlightOption"
					class="pt-6"
					:payByEnrichPoints="props.payByEnrichPoints"
					:hideTodayDateIndicator="props.hideTodayDateIndicator"
					:subHeaderTabDescription="props.mhGeneralDetails?.subHeaderTabDescription"
					:subHeaderTabTooltipBody="props.mhGeneralDetails?.subHeaderTabTooltipBody"
					:subHeaderTabTooltipLabel="props.mhGeneralDetails?.subHeaderTabTooltipLabel"
					:note="props.mhGeneralDetails?.note"
					:searchFlightCTAText="props.mhGeneralDetails?.searchFlightCTAText"
					:ondSelection="props.mhOndSelection"
					:additionalONDFilter="props.mhGeneralDetails?.ondFilter"
					:datePicker="props.mhDatePicker"
					:promoCodeLabel="props.mhPromoCode?.promoCodeLabel"
					:promoCodeErrorMessage="props.mhPromoCode?.promoCodeErrorMessage"
					:supersedePromoCode="props.mhPromoCode?.supersedePromoCode"
					:passengerSelection="props.mhPassengerSelection"
					:subHeaderTooltipCTATextMobileLabel="props.subHeaderTooltipCTATextMobileLabel"
					:fromQuestionMobile="props.fromQuestionMobile"
					:toQuestionMobile="props.toQuestionMobile"
					:flightSearchApiUrl="props.flightSearchApiUrl"
					:cabinClassCashList="props.cabinClassCashList"
					:cabinClassMilesList="props.cabinClassMilesList"
					:togglePointsApiURL="props.togglePointsApiURL"
					:enableMattaToggleButton="props.enableMattaToggleButton"
					:mattaToggleButtonLabel="props.mattaToggleButtonLabel"
					:hidePromoCodeField="props.mhPromoCode?.hidePromoCodeField"
					:enrichAlwaysOn="props.mhEnrichAlwaysOn?.enableFunctionality"
					:enablePostMattaCampaign="props.mhEnrichAlwaysOn?.enablePostMattaCampaign"
					:autoApplyEnrichAlwaysOn="props.mhEnrichAlwaysOn?.autoApplyEnrichAlwaysOn"
					:nonLoggedInMessageNote="props.mhEnrichAlwaysOn?.nonLoggedInMessageNote"
					:loggedInMessageNote="props.mhEnrichAlwaysOn?.loggedInMessageNote"
					:enrichAlwaysOnCountriesData="props.enrichAlwaysOnCountriesData"
					:disableGeolocation="props.disableGeolocation"
					:enableONDLocaleTranslation="props.isEnableLocale"
					:cabinClassMilesOWList="props.cabinClassMilesOWList"
					tabName="redeem-flight"
					:countryVariants="props.countryVariants"
					:mHcustomOndUrl="props.mhGeneralDetails?.customONDURLPath"
					:enableGlassDesign="props.enableGlassDesign"
					:isIgnoreScroll="true"
					@save-search-data="$emit('save-search-data', { isBSTTab: false, ...$event })"
					@form-submission="$emit('form-submission')"
				></SubTabBookFlight>
				<SubTabBookFlight
					v-if="getRedeemTabFlightOptions.some(opt => opt.value === 'ow-redemption')"
					v-show="activeSubOption === 'ow-redemption'"
					ref="subTabOneWorldRef"
					:activeSubOption="selectedFlightOption"
					class="pt-6"
					:payByEnrichPoints="props.payByEnrichPoints"
					:hideTodayDateIndicator="props.hideTodayDateIndicator"
					:subHeaderTabDescription="props.owGeneralDetails?.subHeaderTabDescription"
					:subHeaderTabTooltipBody="props.owGeneralDetails?.subHeaderTabTooltipBody"
					:subHeaderTabTooltipLabel="props.owGeneralDetails?.subHeaderTabTooltipLabel"
					:note="props.owGeneralDetails?.note"
					:searchFlightCTAText="props.owGeneralDetails?.searchFlightCTAText"
					:ondSelection="props.owOndSelection"
					:additionalONDFilter="props.owGeneralDetails?.ondFilter"
					:datePicker="props.owDatePicker"
					:promoCodeLabel="props.owPromoCode?.promoCodeLabel"
					:promoCodeErrorMessage="props.owPromoCode?.promoCodeErrorMessage"
					:supersedePromoCode="props.owPromoCode?.supersedePromoCode"
					:passengerSelection="props.owPassengerSelection"
					:subHeaderTooltipCTATextMobileLabel="props.subHeaderTooltipCTATextMobileLabel"
					:fromQuestionMobile="props.fromQuestionMobile"
					:toQuestionMobile="props.toQuestionMobile"
					:flightSearchApiUrl="props.flightSearchApiUrl"
					:cabinClassCashList="props.cabinClassCashList"
					:cabinClassMilesList="props.cabinClassMilesList"
					:togglePointsApiURL="props.togglePointsApiURL"
					:enableMattaToggleButton="props.enableMattaToggleButton"
					:mattaToggleButtonLabel="props.mattaToggleButtonLabel"
					:hidePromoCodeField="props.owPromoCode?.hidePromoCodeField"
					:enrichAlwaysOn="props.owEnrichAlwaysOn?.enableFunctionality"
					:enablePostMattaCampaign="props.owEnrichAlwaysOn?.enablePostMattaCampaign"
					:autoApplyEnrichAlwaysOn="props.owEnrichAlwaysOn?.autoApplyEnrichAlwaysOn"
					:nonLoggedInMessageNote="props.owEnrichAlwaysOn?.nonLoggedInMessageNote"
					:loggedInMessageNote="props.owEnrichAlwaysOn?.loggedInMessageNote"
					:enrichAlwaysOnCountriesData="props.enrichAlwaysOnCountriesData"
					:disableGeolocation="props.disableGeolocation"
					:enableONDLocaleTranslation="props.isEnableLocale"
					tabName="redeem-flight"
					:countryVariants="props.countryVariants"
					:cabinClassMilesOWList="props.cabinClassMilesOWList"
					:oWcustomOndUrl="props.owGeneralDetails?.customONDURLPath"
					:isIgnoreScroll="true"
					:enableGlassDesign="props.enableGlassDesign"
					@save-search-data="$emit('save-search-data', { isBSTTab: false, ...$event })"
					@form-submission="$emit('form-submission')"
				></SubTabBookFlight>
			</div>
		</div>
		
	</div>

	<Teleport v-if="isLoading" to="body">
		<div class="generic-backdrop !bg-white/80">
			<div class="flex items-center justify-center h-full w-full">
				<AppSpinner />
			</div>
		</div>
	</Teleport>
</div>
</template>


<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.SubTabRedeemFlight {

}
</style>
